import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {
  RestPaginationModel,
  RestQueryParams,
  RestSimpleResponseParser,
  RestYiiFilterService,
  RestYiiPagination,
  RestYiiSort
} from 'gelios-ng-admin-components';
import {BehaviorSubject, Subject, Subscription} from 'rxjs';
import {debounceTime, delay, startWith, switchMap, tap} from 'rxjs/operators';
import {GeliosFieldBaseComponent} from '../gelios-field-base/gelios-field-base.component';
import {TranslateService} from '@ngx-translate/core';
import {ForeignKeyDataService} from '../gelios-foreign-key-field/foreign-key-data.service';
import {AuthorizationService} from '../../../app/authorization/authorization.service';

@Component({
  selector: 'app-gelios-typeahead-field',
  templateUrl: './gelios-typeahead-field.component.html'
})
export class GeliosTypeaheadFieldComponent extends GeliosFieldBaseComponent implements OnInit, OnDestroy {
  public keyField: string;
  public displayedField: string;
  protected foreignKeyDataService: ForeignKeyDataService;
  public items: BehaviorSubject<Array<any>> = new BehaviorSubject<Array<any>>(null);
  public groupBy: ((item: any) => string) | string = null;
  public loading = false;
  public typeahead = new Subject<string>();
  public typeaheadSubscription: Subscription;
  public selectedItem = '';
  private readonly pageSize = 200;

  constructor(
    protected httpClient: HttpClient,
    translateService: TranslateService,
    authorizationService: AuthorizationService,
    @Inject('ENVIRONMENT_VERSION') protected environmentVersion: string,
    @Inject('ENVIRONMENT_API_BASEPOINT') protected environmentApiBasepoint: string
  ) {
    super(translateService);
    this.foreignKeyDataService = new ForeignKeyDataService(
      httpClient,
      new RestYiiFilterService(),
      new RestYiiSort(),
      new RestYiiPagination(),
      new RestSimpleResponseParser(),
      environmentVersion,
      environmentApiBasepoint,
      authorizationService,
    );
    this.typeahead.pipe(
      startWith(''),
      debounceTime(200),
      switchMap(
        text => {
          this.loading = true;
          const restQueryParams: RestQueryParams = new RestQueryParams();
          restQueryParams.filterModel = this.reflexiveField.typeahead.filter || {}; // params.filterModel as RestFilterModel;
          const searchField =
            this.reflexiveField.typeahead.typeAheadSearchField ||
            this.reflexiveField.typeahead.displayedField;
          if (text) {
            restQueryParams.filterModel[searchField] = {
              type: 'contains',
              filterType: 'text',
              filter: text,
              filterTo: null,
            };
          } else if (restQueryParams.filterModel[searchField]) {
            delete restQueryParams.filterModel[searchField];
          }
          restQueryParams.sortModel = []; // params.sortModel as RestSortModel;
          restQueryParams.paginationModel.pageSize = this.pageSize;
          restQueryParams.paginationModel.page = 1;
          return this.foreignKeyDataService.getAll(restQueryParams);
        }
      ),
      tap(() => this.loading = false)
    ).subscribe(
      response => this.items.next(response.body)
    );
  }

  ngOnInit() {
    super.ngOnInit();
    this.groupBy = this.reflexiveField.typeahead.groupBy ? this.reflexiveField.typeahead.groupBy : null;
    this.keyField = this.reflexiveField.typeahead.keyField;
    this.displayedField = this.reflexiveField.typeahead.displayedField;
    this.foreignKeyDataService
      .setFields(this.reflexiveField.typeahead.fields || '')
      .setExpand(this.reflexiveField.typeahead.expand || '')
      .setModelName(this.reflexiveField.typeahead.modelName)
      .setSearchModelName(this.reflexiveField.typeahead.searchModelName);
    // this.fetchItems();
    // this.typeaheadSubscription = this.typeahead.subscribe(
    //   text =>
    //
    // )
    if (this.reflexiveField.value) {
      this.loading = true;
      const restQueryParams: RestQueryParams = new RestQueryParams();
      restQueryParams.filterModel = JSON.parse(JSON.stringify(this.reflexiveField.typeahead.filter || {}));
      restQueryParams.filterModel[this.reflexiveField.typeahead.keyField] = {
        type: 'equals',
        filterType: 'text',
        filter: this.reflexiveField.value,
        filterTo: null,
      };
      restQueryParams.sortModel = []; // params.sortModel as RestSortModel;
      this.foreignKeyDataService.getAll(restQueryParams).pipe(
        delay(0),
      ).subscribe(
        response => {
          this.loading = false;
          this.items.next(response.body);
        }
      );
    } else {
      this.typeahead.next('');
    }
    if (this.reflexiveField.typeahead.filterBehaviorSubject) {
      this.reflexiveField.typeahead.filterBehaviorSubject.subscribe(
        f => {
          this.reflexiveField.typeahead.filter = f;
          this.fetchItems();
        }
      );
    }
  }

  protected fetchItems(): any {
    this.loading = true;
    const restQueryParams: RestQueryParams = new RestQueryParams();
    restQueryParams.filterModel = this.reflexiveField.typeahead.filter;
    restQueryParams.sortModel = this.reflexiveField.typeahead.sort ? this.reflexiveField.typeahead.sort : [];
    const pageSize = 100;
    const page = 1;
    restQueryParams.paginationModel = {pageSize: pageSize, page: page} as RestPaginationModel;
    this.foreignKeyDataService
      .getAll(restQueryParams)
      .pipe(
        delay(0),
        tap(() => this.loading = false),
      ).subscribe(
      response => {
        this.loading = false;
        this.items.next(response.body);
      }
    );
  }

  public isMultiple() {
    return !!this.reflexiveField.typeahead.multiple;
  }

  ngOnDestroy(): void {
    if (this.typeaheadSubscription) {
      this.typeaheadSubscription.unsubscribe();
    }
  }

  onValueChange(event) {
    super.onValueChange(event);
  }

  onClear(event) {
    this.typeahead.next('');
  }
}
